import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { keyframes } from "@emotion/react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useColorScheme, useTheme, type Theme } from "@mui/material/styles";
import { useNewNotificationCount } from "../api/queries";
import { PillarsAssist } from "../pillars-assist";
import { Icon } from "../ui/Icon";
import { PrimeGlyph } from "../ui/PrimeGlyph";

// PORT OF `src/app/_layout/_topbar` AND ITS STYLES (`assets/styles/layout/_topbar.scss`),
// MEASURED AGAINST THE RUNNING ANGULAR APP. ANGULAR RUNS AT A 14PX ROOT (`scale: 14`),
// SO ITS REM VALUES ARE WRITTEN HERE AS THE PIXELS THEY RESOLVE TO: 5REM BAR = 70PX,
// 3REM BUTTONS = 42PX, 1.5REM ICONS AND LOGO TEXT = 21PX.
//
// COLORS ARE THE PILLARS THEME TOKENS (`pillars-light` / `pillars-dark` THEME.CSS).
const TOKENS = {
  light: { secondary: "#6c757d", text: "#212529", hover: "#e9ecef", badgeBg: "#ffc107", badgeText: "#212529" },
  dark: { secondary: "rgba(255, 255, 255, 0.6)", text: "rgba(255, 255, 255, 0.87)", hover: "rgba(255, 255, 255, 0.04)", badgeBg: "#ffe082", badgeText: "#151515" },
};
const SHADOW = "0px 3px 5px rgba(0, 0, 0, 0.02), 0px 0px 2px rgba(0, 0, 0, 0.05), 0px 1px 4px rgba(0, 0, 0, 0.08)";

// PRIMEFLEX'S `scalein`, WHICH THE PHONE MENU OPENS WITH
const scalein = keyframes`
  0% { opacity: 0; transform: scaleY(0.8); }
  100% { opacity: 1; transform: scaleY(1); }
`;

// THE LAYOUT SWITCHES AT ANGULAR'S `max-width: 991px`, NOT AN MUI BREAKPOINT
const COMPACT = "(max-width: 991px)";

// `.layout-topbar-button` / `.layout-topbar-menu-btn`: A 42PX ROUND, SECONDARY-COLORED
// BUTTON THAT DARKENS ON HOVER. NO RIPPLE - PRIMENG'S `p-link` HAS NONE.
const roundButton = (theme: Theme) => ({
  position: "relative" as const,
  width: 42,
  height: 42,
  flexShrink: 0,
  borderRadius: "50%",
  color: TOKENS.light.secondary,
  transition: "background-color 0.2s",
  "&:hover": { color: TOKENS.light.text, bgcolor: TOKENS.light.hover },
  "&.Mui-focusVisible": { boxShadow: `0 0 0 0.2rem ${theme.palette.primary.main}40` },
  ...theme.applyStyles("dark", {
    color: TOKENS.dark.secondary,
    "&:hover": { color: TOKENS.dark.text, bgcolor: TOKENS.dark.hover },
  }),
});

// `.header-badge > .p-badge`: 21PX AMBER CIRCLE, CENTERED ON THE BUTTON'S RIGHT EDGE,
// TOP-ALIGNED WITH IT. EMPTY (ZERO) BADGES ARE HIDDEN, AS `:empty { display: none }`.
function CountBadge({ count, theme }: { count: number; theme: Theme }) {
  if (count <= 0) return null;
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        position: "absolute",
        top: "10px",
        right: 0,
        transform: "translate(50%, -50%)",
        minWidth: 21,
        height: 21,
        px: "5px",
        borderRadius: "10.5px",
        bgcolor: TOKENS.light.badgeBg,
        color: TOKENS.light.badgeText,
        fontSize: "10.5px",
        fontWeight: 700,
        lineHeight: "21px",
        textAlign: "center",
        ...theme.applyStyles("dark", { bgcolor: TOKENS.dark.badgeBg, color: TOKENS.dark.badgeText }),
      }}
    >
      {count > 99 ? "99+" : count}
    </Box>
  );
}

export function TopBar({ onMenu, onLogout }: { onMenu: () => void; onLogout: () => void }) {
  const theme = useTheme();
  const compact = useMediaQuery(COMPACT);
  const { mode, setMode } = useColorScheme();
  const dark = mode === "dark";
  const toggleTheme = () => setMode(dark ? "light" : "dark");
  const count = useNewNotificationCount().data ?? 0;
  const [menuOpen, setMenuOpen] = useState(false);

  // THE FOUR ACTIONS, SHARED BY THE DESKTOP ROW AND THE PHONE CARD. THE THEME ICON
  // SHOWS THE CURRENT MODE, AS ANGULAR'S DOES.
  const actions: Array<{ key: string; icon: string; label: string; tooltip: string; badge?: number; to?: string; onClick?: () => void }> = [
    { key: "tasks", icon: "task_alt", label: "Daily Tasks", tooltip: "Daily Tasks", to: "/daily-tasks" },
    { key: "notifications", icon: "notifications_active", label: "Notifications", tooltip: "Notifications", badge: count, to: "/notifications" },
    { key: "theme", icon: dark ? "dark_mode" : "light_mode", label: "Theme", tooltip: "Change Theme", onClick: toggleTheme },
    { key: "logout", icon: "logout", label: "Logout", tooltip: "Logout", onClick: onLogout },
  ];

  const linkProps = (a: (typeof actions)[number]) => (a.to ? { component: Link, to: a.to } : {});

  const logo: ReactNode = (
    <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, order: compact ? 2 : 0 }}>
      {/* THE LOGO IS THE PILLARS ASSIST TRIGGER, AS IN ANGULAR */}
      <PillarsAssist />
      <Box
        component={Link}
        to="/"
        sx={{
          color: "primary.main",
          textDecoration: "none",
          fontSize: "21px",
          fontWeight: 500,
          lineHeight: 1,
          whiteSpace: "nowrap",
          "@media (max-width: 550px)": { fontSize: "16.8px" },
        }}
      >
        <span>Pillars: </span>
        {/* PRIMEFLEX `ml-2` / `mr-4` = 0.5REM / 1.5REM, KEPT ON PHONES TOO - IT IS WHAT OFFSETS THE CENTERED LOGO LEFT */}
        <Box component="span" sx={{ ml: "7px", mr: "21px" }}>
          HOST PRM
        </Box>
      </Box>
    </Box>
  );

  const menuButton = (
    <ButtonBase disableRipple aria-label="Open menu" onClick={onMenu} sx={[roundButton(theme), { order: compact ? 1 : 0, ml: compact ? 0 : "28px" }]}>
      {/* `pi-bars` AT 1.5REM */}
      <PrimeGlyph name="bars" size={21} />
    </ButtonBase>
  );

  return (
    <Box
      component="header"
      // `.layout-topbar`: FIXED, 5REM TALL, FULL WIDTH; `.layout-main-container`'S 6REM TOP
      // PADDING CLEARS IT (Z-INDEX 997, UNDER THE MENU'S 999)
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        zIndex: 997,
        height: 70,
        px: "14px",
        display: "flex",
        alignItems: "center",
        bgcolor: "background.paper",
        boxShadow: SHADOW,
      }}
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          maxWidth: 1515,
          mx: "auto",
          px: "14px",
        }}
      >
        {logo}
        {menuButton}

        {compact ? (
          // PHONES AND TABLETS: `pi-ellipsis-v` OPENS THE LABELED CARD BELOW THE BAR
          <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
            <Box sx={{ order: 3 }}>
              <ButtonBase
                disableRipple
                aria-label="More actions"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((o) => !o)}
                onKeyDown={(e) => e.key === "Escape" && setMenuOpen(false)}
                sx={{
                  ...roundButton(theme),
                  // ANGULAR SHOWS THE BUTTON IN ITS HOVER STATE WHILE THE CARD IS OPEN
                  ...(menuOpen
                    ? { color: TOKENS.light.text, bgcolor: TOKENS.light.hover, ...theme.applyStyles("dark", { color: TOKENS.dark.text, bgcolor: TOKENS.dark.hover }) }
                    : {}),
                }}
              >
                {/* NO UNREAD DOT HERE: ANGULAR SHOWS THE COUNT ONLY INSIDE THE OPEN CARD */}
                {/* `pi-ellipsis-v` AT 1.25REM */}
                <PrimeGlyph name="ellipsis-v" size={17.5} />
              </ButtonBase>
              {menuOpen && (
                <Box
                  role="menu"
                  onKeyDown={(e) => e.key === "Escape" && setMenuOpen(false)}
                  sx={{
                    position: "absolute",
                    top: 56,
                    right: "14px",
                    // SHRINK TO THE ITEMS, NEVER BELOW ANGULAR'S 15REM
                    width: "max-content",
                    minWidth: 210,
                    p: "14px",
                    borderRadius: "12px",
                    bgcolor: "background.paper",
                    boxShadow: SHADOW,
                    transformOrigin: "top",
                    animation: `${scalein} 0.15s linear`,
                  }}
                >
                  {actions.map((a) => (
                    <ButtonBase
                      key={a.key}
                      role="menuitem"
                      disableRipple
                      {...linkProps(a)}
                      onClick={() => {
                        a.onClick?.();
                        // THE THEME ITEM LEAVES THE CARD OPEN SO THE CHANGE IS VISIBLE
                        if (a.key !== "theme") setMenuOpen(false);
                      }}
                      sx={[
                        roundButton(theme),
                        { display: "flex", width: "100%", height: "auto", justifyContent: "flex-start", borderRadius: "12px", p: "14px", fontSize: "14px", fontFamily: "inherit" },
                      ]}
                    >
                      <Icon name={a.icon} size={21} style={{ marginRight: 7 }} />
                      <span>{a.label}</span>
                      {a.badge !== undefined && <CountBadge count={a.badge} theme={theme} />}
                    </ButtonBase>
                  ))}
                </Box>
              )}
            </Box>
          </ClickAwayListener>
        ) : (
          // DESKTOP: A RIGHT-ALIGNED ROW OF ROUND ICON BUTTONS, 14PX APART
          <Box component="nav" aria-label="Quick actions" sx={{ display: "flex", ml: "auto" }}>
            {actions.map((a) => (
              <Tooltip key={a.key} title={a.tooltip} placement="bottom">
                <ButtonBase disableRipple aria-label={a.key === "theme" ? (dark ? "Switch to light mode" : "Switch to dark mode") : a.label} {...linkProps(a)} onClick={a.onClick} sx={[roundButton(theme), { ml: "14px" }]}>
                  <Icon name={a.icon} size={21} />
                  {a.badge !== undefined && <CountBadge count={a.badge} theme={theme} />}
                </ButtonBase>
              </Tooltip>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
